import { useContext, useState, useEffect } from "react";
import ScrollToTop from "../../components/ScrollToTop";
import { useParams } from "react-router-dom";
import {
  FaWhatsapp,
  FaMapMarkerAlt,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaRegCommentDots,
  FaShieldAlt,
} from "react-icons/fa";
import { BiImages } from "react-icons/bi";
import ImageLightbox from "../../components/ImageLightbox/ImageLightbox";
import { Helmet } from "react-helmet";
import { HouseContext } from "../../context/HouseContext";
import { BounceLoader } from "react-spinners";
import Map from "../../components/Map";
import { getCharIcon } from "../../utils/characteristicIcons";

const PropertyDetails = () => {
  const { houses } = useContext(HouseContext);
  const { id } = useParams();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [property, setProperty] = useState(null);

  useEffect(() => {
    const foundProperty = houses.find((house) => house.id === id);
    setProperty(foundProperty);
  }, [id, houses]);

  if (!property) {
    return (
      <div className="flex flex-col justify-center items-center h-[90vh]">
        <BounceLoader className="my-2" color="#187795" size={60} />
        <h2 className="text-xl">Cargando Propiedad</h2>
      </div>
    );
  }

  const openLightbox = (index) => setLightboxIndex(index);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const isFormComplete = () => {
    return (
      formData.name && formData.email && formData.phone && formData.message
    );
  };

  const formatPrice = () =>
    property.currency && property.price
      ? `${property.currency} ${Number(property.price).toLocaleString("es-AR")}`
      : "Consultar";

  const handleSubmit = (e) => {
    e.preventDefault();

    const whatsappMessage = encodeURIComponent(`\
      *Consulta sobre la propiedad* \n
      *Nombre*: ${formData.name} \n
      *Email*: ${formData.email} \n
      *Teléfono*: ${formData.phone} \n
      *Mensaje*: ${formData.message} \n\n
      *Detalles de la propiedad* \n
      *Nombre*: ${property.name} \n
      *Dirección*: ${property.address} \n
      *Localidad*: ${property.country} \n
      *Tipo*: ${property.type} \n
      *Precio*: ${property.currency} ${
      property.price !== "" ? property.price : "Consultar"
    } \n
      *Descripción*: ${property.description}
    `);

    const whatsappLink = `https://wa.me/+5492241460121?text=${whatsappMessage}`;
    window.open(whatsappLink, "_blank");
  };

  const inputClass =
    "border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl w-full pl-11 pr-4 h-12 text-sm outline-none transition-all";

  return (
    <div className="container mx-auto min-h-[800px] mt-32 mb-20 px-4">
      <Helmet>
        <title>{property.name}</title>
        <meta
          name="description"
          content={`Consulta sobre ${property.name}. ${property.description}`}
        />
        <meta
          name="keywords"
          content={`${property.name}, ${property.type}, ${property.country}, propiedades, alquiler`}
        />
      </Helmet>
      <ScrollToTop />

      {/* Header */}
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2.5">
            {property.type && <span>{property.type}</span>}
            {property.type && property.country && (
              <span className="text-gray-300">•</span>
            )}
            {property.country && <span>{property.country}</span>}
          </div>
          <h1 className="text-3xl lg:text-[2.6rem] leading-tight font-bold text-gray-900">
            {property.name}
          </h1>
          <div className="flex items-center gap-1.5 text-gray-500 mt-2.5">
            <FaMapMarkerAlt className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-base">{property.address}</span>
          </div>
        </div>
        <div className="flex-shrink-0 sm:text-right">
          {property.state && (
            <span className="inline-block text-[11px] font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2.5 py-1 rounded-full mb-2">
              {property.state}
            </span>
          )}
          <p className="text-3xl lg:text-4xl font-extrabold text-gray-900">
            {property.currency && property.price ? (
              <>
                <span className="text-lg font-semibold text-gray-400 mr-1">
                  {property.currency}
                </span>
                {Number(property.price).toLocaleString("es-AR")}
              </>
            ) : (
              "Consultar"
            )}
          </p>
        </div>
      </div>

      {/* Galería estilo mosaico */}
      {property.images && property.images.length > 0 ? (
        <div className="relative mb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 grid-rows-1 gap-2 h-[340px] sm:h-[480px] rounded-3xl overflow-hidden">
            {/* Imagen principal */}
            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="group relative h-full w-full min-h-0 overflow-hidden"
            >
              <img
                src={property.images[0]}
                alt={`${property.name} - 1`}
                className="absolute inset-0 w-full h-full object-cover cursor-pointer transition duration-300 group-hover:brightness-90"
              />
            </button>

            {/* Grid con las siguientes fotos: se adapta a cuántas haya */}
            {property.images.length > 1 &&
              (() => {
                const extras = property.images.slice(1, 5);
                const layout = {
                  1: "grid-cols-1 grid-rows-1",
                  2: "grid-cols-1 grid-rows-2",
                  3: "grid-cols-2 grid-rows-2",
                  4: "grid-cols-2 grid-rows-2",
                }[extras.length];
                return (
                  <div className={`hidden sm:grid ${layout} gap-2 h-full min-h-0`}>
                    {extras.map((image, index) => (
                      <button
                        type="button"
                        key={index}
                        onClick={() => openLightbox(index + 1)}
                        className={`group relative h-full w-full min-h-0 overflow-hidden ${
                          extras.length === 3 && index === 0 ? "col-span-2" : ""
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${property.name} - ${index + 2}`}
                          className="absolute inset-0 w-full h-full object-cover cursor-pointer transition duration-300 group-hover:brightness-90"
                        />
                      </button>
                    ))}
                  </div>
                );
              })()}
          </div>

          {/* Botón ver todas las fotos */}
          {property.images.length > 1 && (
            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="absolute bottom-4 right-4 flex items-center gap-2 bg-white/95 hover:bg-white text-gray-800 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-md border border-gray-200 transition-colors"
            >
              <BiImages className="w-4 h-4" />
              Ver las {property.images.length} fotos
            </button>
          )}
        </div>
      ) : (
        <div className="h-[340px] sm:h-[480px] rounded-3xl bg-gray-100 flex items-center justify-center text-gray-400 mb-10">
          Sin imágenes
        </div>
      )}

      {lightboxIndex !== null && (
        <ImageLightbox
          images={property.images}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Contenido + Contacto */}
      <div className="flex flex-col gap-10 lg:flex-row lg:items-start">
        {/* Columna principal */}
        <div className="w-full lg:w-[62%] space-y-12">
          {/* Descripción */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 pb-3 mb-5 border-b border-gray-200">
              Descripción
            </h2>
            <p className="text-gray-600 text-[15px] leading-relaxed whitespace-pre-line">
              {property.description}
            </p>
          </section>

          {/* Características */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 pb-3 mb-6 border-b border-gray-200">
              Características
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(property.characteristics)
                .filter(([, value]) => value)
                .map(([key, value], i) => {
                  const Icon = getCharIcon(key);
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-4 rounded-2xl border border-gray-100 bg-white hover:border-primary/30 hover:shadow-sm transition-all"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 capitalize truncate">
                          {key}
                        </p>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {value}
                        </p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>

          {/* Ubicación */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 pb-3 mb-6 border-b border-gray-200">
              Ubicación
            </h2>
            <div className="rounded-3xl overflow-hidden border border-gray-100">
              <Map src={property.maps} />
            </div>
          </section>
        </div>

        {/* Contacto sticky */}
        <div className="w-full lg:w-[38%] lg:sticky lg:top-32 lg:self-start">
          <div className="bg-white rounded-3xl shadow-lg shadow-gray-200/60 border border-gray-100 overflow-hidden">
            {/* Encabezado con precio destacado */}
            <div className="bg-gradient-to-br from-primary to-secondary px-6 py-5 text-white">
              <p className="text-[11px] uppercase tracking-wide text-white/70 mb-1">
                {property.state || "Precio"}
              </p>
              <p className="text-3xl font-extrabold leading-none">
                {formatPrice()}
              </p>
            </div>

            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-1">
                Consultá por esta propiedad
              </h2>
              <p className="text-sm text-gray-400 mb-5">
                Te respondemos por WhatsApp a la brevedad
              </p>
              <form className="flex flex-col gap-y-3" onSubmit={handleSubmit}>
                <div className="relative">
                  <FaUser className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                  <input
                    className={inputClass}
                    type="text"
                    name="name"
                    placeholder="Nombre*"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="relative">
                  <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                  <input
                    className={inputClass}
                    type="email"
                    name="email"
                    placeholder="Email*"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="relative">
                  <FaPhoneAlt className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                  <input
                    className={inputClass}
                    type="text"
                    name="phone"
                    placeholder="Teléfono*"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="relative">
                  <FaRegCommentDots className="absolute left-3.5 top-4 w-4 h-4 text-gray-300" />
                  <textarea
                    className="border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl w-full pl-11 pr-4 py-3.5 h-32 text-sm outline-none resize-none transition-all"
                    name="message"
                    placeholder="Mensaje*"
                    value={formData.message}
                    onChange={handleInputChange}
                  />
                </div>
                <button
                  className={`${
                    isFormComplete()
                      ? "bg-green-500 hover:bg-green-600 shadow-md shadow-green-500/30"
                      : "bg-gray-300 cursor-not-allowed"
                  } text-white rounded-xl p-3.5 text-sm font-semibold w-full transition-all flex justify-center items-center gap-2`}
                  type="submit"
                  disabled={!isFormComplete()}
                >
                  <FaWhatsapp className="text-xl" />
                  Enviar por WhatsApp
                </button>
              </form>
              <p className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-4">
                <FaShieldAlt className="w-3 h-3" />
                Respuesta en el día · Sin compromiso
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp flotante */}
      <a
        href={`https://wa.me/+5492241460121?text=${encodeURIComponent(
          `Hola, me interesa la propiedad "${property.name}" (${property.address}). Precio: ${formatPrice()}.`
        )}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Consultar por WhatsApp"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg flex items-center justify-center transition-colors"
      >
        <FaWhatsapp className="w-7 h-7" />
      </a>
    </div>
  );
};

export default PropertyDetails;
